import type { FieldOf } from "@fobu/schema";
import { t } from "../../lib/i18n";
import type { FieldProps } from "./types";

const range = (from: number, to: number) => Array.from({ length: to - from + 1 }, (_, i) => from + i);

function Icon({ kind }: { kind: "star" | "heart" | "thumb" }) {
  const paths = {
    star: "M12 2.8l2.8 5.9 6.4.8-4.7 4.4 1.2 6.4L12 17.2l-5.7 3.1 1.2-6.4-4.7-4.4 6.4-.8z",
    heart: "M12 20.5s-7.5-4.6-9.2-9.4C1.6 7.6 3.9 4.5 7.2 4.5c2 0 3.5 1.1 4.8 2.8 1.3-1.7 2.8-2.8 4.8-2.8 3.3 0 5.6 3.1 4.4 6.6-1.7 4.8-9.2 9.4-9.2 9.4z",
    thumb: "M7 10v10H3V10zm2 10V9.5L13.5 3c1.4 0 2.3 1.2 2 2.5L14.8 9H20a2 2 0 0 1 2 2.3l-1.3 7A2 2 0 0 1 18.7 20z",
  };
  return (
    <svg viewBox="0 0 24 24" width="32" height="32" aria-hidden="true">
      <path d={paths[kind]} />
    </svg>
  );
}

/** Gemeinsame Basis: Radio-Gruppe mit Zahlenwerten, als Segmentleiste. */
function NumberScale({
  name,
  values,
  value,
  onPick,
  render,
  ariaLabel,
  minLabel,
  midLabel,
  maxLabel,
  variant,
}: {
  name: string;
  values: number[];
  value: unknown;
  onPick: (n: number) => void;
  render?: (n: number, active: boolean) => preact.ComponentChildren;
  ariaLabel: (n: number) => string;
  minLabel?: string;
  midLabel?: string;
  maxLabel?: string;
  variant: "segments" | "icons";
}) {
  const current = typeof value === "number" ? value : null;
  return (
    <div class={`fb-scale fb-scale--${variant}`}>
      {/* Bis 11 Stufen (z. B. NPS 0–10) immer in einer Reihe – sonst verlieren die Endbeschriftungen ihren Bezug. */}
      <div class={`fb-scale-row${variant === "segments" && values.length <= 11 ? " fb-scale-row--single" : ""}`} style={{ "--fb-count": values.length }}>
        {values.map((n) => {
          const checked = current === n;
          const active = variant === "icons" ? current !== null && n <= current : checked;
          return (
            <label key={n} class={`fb-scale-item${checked ? " is-checked" : ""}${active ? " is-active" : ""}`}>
              <input class="fb-option-input" type="radio" name={name} value={n} checked={checked} onChange={() => onPick(n)} aria-label={ariaLabel(n)} />
              {render ? render(n, active) : <span aria-hidden="true">{n}</span>}
            </label>
          );
        })}
      </div>
      {(minLabel || maxLabel || midLabel) && (
        <div class="fb-scale-labels" aria-hidden="true">
          <span>{minLabel}</span>
          <span>{midLabel}</span>
          <span>{maxLabel}</span>
        </div>
      )}
    </div>
  );
}

export function Rating({ field, value, onChange, a11y, conversational, onAdvance }: FieldProps<FieldOf<"rating">>) {
  const pick = (n: number) => {
    onChange(n);
    if (conversational) onAdvance();
  };
  return (
    <NumberScale
      name={a11y.id}
      values={range(1, field.max)}
      value={value}
      onPick={pick}
      variant={field.icon === "number" ? "segments" : "icons"}
      render={field.icon === "number" ? undefined : () => <Icon kind={field.icon as "star" | "heart" | "thumb"} />}
      ariaLabel={(n) => t.ratingLabel(n, field.max)}
    />
  );
}

export function Nps({ field, value, onChange, a11y, conversational, onAdvance }: FieldProps<FieldOf<"nps">>) {
  return (
    <NumberScale
      name={a11y.id}
      values={range(0, 10)}
      value={value}
      onPick={(n) => {
        onChange(n);
        if (conversational) onAdvance();
      }}
      variant="segments"
      ariaLabel={(n) => (n === 0 ? `0 – ${field.minLabel}` : n === 10 ? `10 – ${field.maxLabel}` : String(n))}
      minLabel={field.minLabel}
      maxLabel={field.maxLabel}
    />
  );
}

export function Scale({ field, value, onChange, a11y, conversational, onAdvance }: FieldProps<FieldOf<"scale">>) {
  const values = range(field.min, field.max);
  return (
    <NumberScale
      name={a11y.id}
      values={values}
      value={value}
      onPick={(n) => {
        onChange(n);
        if (conversational) onAdvance();
      }}
      variant="segments"
      ariaLabel={(n) =>
        n === field.min && field.minLabel ? `${n} – ${field.minLabel}` : n === field.max && field.maxLabel ? `${n} – ${field.maxLabel}` : String(n)
      }
      minLabel={field.minLabel}
      midLabel={field.midLabel}
      maxLabel={field.maxLabel}
    />
  );
}

export function Slider({ field, value, onChange, a11y }: FieldProps<FieldOf<"slider">>) {
  const touched = typeof value === "number";
  const v = touched ? (value as number) : field.min;
  const pct = ((v - field.min) / (field.max - field.min)) * 100;
  const text = `${v.toLocaleString("de-DE")}${field.unit ? ` ${field.unit}` : ""}`;
  return (
    <div class={`fb-slider${touched ? "" : " is-untouched"}`}>
      <output class="fb-slider-out" for={a11y.id} aria-hidden="true">
        {touched ? text : "–"}
      </output>
      <input
        class="fb-range"
        id={a11y.id}
        type="range"
        min={field.min}
        max={field.max}
        step={field.step}
        value={v}
        style={{ "--fb-pct": `${pct}%` }}
        onInput={(e) => onChange(Number(e.currentTarget.value))}
        aria-valuetext={text}
        aria-invalid={a11y.invalid || undefined}
        aria-describedby={a11y.describedBy}
      />
      <div class="fb-scale-labels" aria-hidden="true">
        <span>
          {field.min.toLocaleString("de-DE")} {field.unit}
        </span>
        <span />
        <span>
          {field.max.toLocaleString("de-DE")} {field.unit}
        </span>
      </div>
    </div>
  );
}
